import { FleetAreaCode, formatCentAsEuro } from '@gb-ticket/shared-types';
import { useMemo, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDeDate } from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import { fleetAreaOn, useFleetAreas } from '@/products/fleet/features/use-fleet-areas';
import { useFleetVehicleCosts } from '@/products/fleet/features/use-fleet-fuel';

function monthBounds(ym: string): { from: string; to: string } {
  const [yearText, monthText] = ym.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const from = `${ym}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const to = `${ym}-${String(lastDay).padStart(2, '0')}`;
  return { from, to };
}

function currentMonthYm(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${m}`;
}

export function FleetVehicleCostsTab({ vehicleId }: { vehicleId: string }) {
  const [month, setMonth] = useState(currentMonthYm);
  const range = useMemo(() => monthBounds(month), [month]);
  const areas = useFleetAreas();
  const catalog = areas.isError ? undefined : areas.data;
  const workshopOn = fleetAreaOn(catalog, FleetAreaCode.WORKSHOP);
  const fuelOn = fleetAreaOn(catalog, FleetAreaCode.FUEL);
  const costs = useFleetVehicleCosts(vehicleId, range);
  const lines = (costs.data?.items ?? []).filter(
    (line) => (line.kind === 'workshop' && workshopOn) || (line.kind === 'fuel' && fuelOn),
  );

  return (
    <div className="space-y-6">
      <div className={`${fleetPanelClassName} max-w-xs`}>
        <Label htmlFor="vehicle-cost-month">Monat</Label>
        <Input
          id="vehicle-cost-month"
          className="mt-2"
          type="month"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
      </div>
      {costs.isPending ? (
        <p className="text-sm text-muted-foreground">Kosten werden geladen …</p>
      ) : null}
      {costs.data ? (
        <dl className={`${fleetPanelClassName} grid gap-2 text-sm sm:grid-cols-3`}>
          {workshopOn ? (
            <div>
              <dt className="text-muted-foreground">Werkstatt</dt>
              <dd>{formatCentAsEuro(costs.data.workshopCents)}</dd>
            </div>
          ) : null}
          {fuelOn ? (
            <div>
              <dt className="text-muted-foreground">Kraftstoff</dt>
              <dd>{formatCentAsEuro(costs.data.fuelCents)}</dd>
            </div>
          ) : null}
          <div>
            <dt className="text-muted-foreground">Summe</dt>
            <dd className="font-medium">
              {formatCentAsEuro(
                (workshopOn ? costs.data.workshopCents : 0) + (fuelOn ? costs.data.fuelCents : 0),
              )}
            </dd>
          </div>
        </dl>
      ) : null}
      {lines.length > 0 ? (
        <div className={fleetTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Art</TableHead>
                <TableHead>Bezeichnung</TableHead>
                <TableHead>Betrag</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={`${line.kind}-${line.id}`}>
                  <TableCell>{formatDeDate(line.occurredOn)}</TableCell>
                  <TableCell>{line.kind === 'workshop' ? 'Werkstatt' : 'Kraftstoff'}</TableCell>
                  <TableCell>{line.label}</TableCell>
                  <TableCell>{formatCentAsEuro(line.amountCents)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </div>
  );
}
